<ng-container *ngIf="domains">
  <div class="flex justify-between items-center mb-8">
    <h2 class="nomargin">Domains</h2>

    <ng-container *ngIf="!detailedDomain">
      <sbnb-button size="mini" label="Add your own domain" (click)="openAddDomainModal()"></sbnb-button>
    </ng-container>
    <ng-container *ngIf="detailedDomain">
      <div class="flex items-center space-x-2">
        <!-- Unlink Button -->
        <sbnb-button
          size="mini"
          type="secondary"
          label="Unlink custom domain"
          (clicked)="confirmUnlinkCustomDomain()"
          [loading]="removingDomain"></sbnb-button>
        <!-- Verify Button (New Location - Right) -->
        <ng-container *ngIf="!detailedDomain.dnsManaged">
          <sbnb-button
            size="mini"
            type="primary"
            label="Verify site"
            (clicked)="triggerVerification()"
            [loading]="isVerifyingDns"></sbnb-button>
        </ng-container>
      </div>
    </ng-container>
  </div>

  <ng-container *ngIf="detailedDomain">
    <div class="flex justify-between items-center mb-4">
      <div class="text-ink text-lg">
        Connected domain: <strong class="font-medium">{{ detailedDomain.name }}</strong>
      </div>
    </div>

    <ng-container *ngIf="detailedDomain.dnsManaged">
      <ng-container *ngIf="detailedDomain.nameserverVerifiedAt">
        <sbnb-direct-domain-description
          [isPublished]="directEditService.isPublished"
          [url]="'https://' + detailedDomain.name" />
      </ng-container>
      <ng-container *ngIf="!detailedDomain.nameserverVerifiedAt">
        <sbnb-unlinked-managed-domain-meta
          class="block mt-8"
          [hideElevation]="true"
          [domainUuid]="detailedDomain.uuid"
          [domainName]="detailedDomain.name"
          (domainVerified)="reloadDomainDetails()"></sbnb-unlinked-managed-domain-meta>
      </ng-container>
    </ng-container>
    <ng-container *ngIf="!detailedDomain.dnsManaged">
      <!-- Externally Managed Domain Section -->

      <!-- Show Loading State -->
      <div *ngIf="isVerifyingDns" class="flex justify-center items-center p-10 border border-grey-lighter rounded mt-8 bg-grey-lightest">
        <mat-spinner diameter="40"></mat-spinner>
        <span class="ml-4 text-ink">Verifying DNS records...</span>
      </div>

      <!-- Show Instructions Block if NOT verified successfully AND NOT currently verifying -->
      <ng-container *ngIf="!dnsVerifiedSuccessfully && !isVerifyingDns">
        <div class="mt-8 bg-grey-lightest border border-grey-lighter rounded p-6">
          <h3 class="text-lg font-medium mb-2">One more step before we can use {{ detailedDomain.name }}...</h3>
          <p class="text-ink mb-6">Follow the instructions below to to point your DNS records to Hospitable</p>

          <!-- Step 1 -->
          <h4 class="text-base font-medium mt-6 mb-2">Step 1: Log in to your domain registrar</h4>
          <p class="text-ink mb-6">
            Log in to the account where your domain is registered (e.g., GoDaddy, Namecheap, Google Domains) and find the
            option to edit your DNS records. This varies from provider to provider.
          </p>

          <!-- Step 2: Display Conflicting Records -->
          @if (conflictingDnsRecords.length > 0) {
            <h4 class="text-base font-medium mt-6 mb-2 text-warning-dark">Step 2: Resolve these conflicting records</h4>
            <p class="text-sm text-warning-dark mb-4">Our system detected the following DNS records that conflict with the required setup. Please remove or update these records at your domain registrar:</p>
            <div class="overflow-x-auto relative border border-warning-light rounded">
              <table class="w-full text-sm text-left text-ink table-fixed">
                <thead class="text-xs text-smoke-dark uppercase bg-warning-lightest">
                  <tr>
                    <th scope="col" class="py-3 px-6 w-1/3">Type</th>
                    <th scope="col" class="py-3 px-6 w-1/3">Name</th>
                    <th scope="col" class="py-3 px-6 w-1/3">Conflicting Value</th>
                    <th scope="col" class="py-3 px-6 w-12"></th> <!-- Empty spacer column -->
                  </tr>
                </thead>
                <tbody>
                  @for (record of conflictingDnsRecords; track record) {
                    <tr class="bg-white border-b border-grey-lighter last:border-b-0">
                      <td class="py-4 px-6 w-1/3 font-mono">{{ record.type }}</td>
                      <td class="py-4 px-6 w-1/3 font-mono">{{ record.name }}</td>
                      <td class="py-4 px-6 w-1/3 font-mono">{{ record.value }}</td>
                      <td class="py-4 px-6 w-12"></td> <!-- Empty spacer cell -->
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }

          <!-- Determine if root or subdomain -->
          <ng-container *ngIf="detailedDomain.name.split('.').length <= 2">
            <!-- Step 3: Root Domain (A Record) -->
            <h4 class="text-base font-medium mt-6 mb-2">Step 3: Add these new DNS records</h4>
            <div class="overflow-x-auto relative border border-grey-lighter rounded">
              <table class="w-full text-sm text-left text-ink table-fixed">
                <thead class="text-xs text-smoke-dark uppercase bg-grey-lightest">
                  <tr>
                    <th scope="col" class="py-3 px-6 w-1/3">Type</th>
                    <th scope="col" class="py-3 px-6 w-1/3">Name</th>
                    <th scope="col" class="py-3 px-6 w-1/3">Current Value</th>
                    <th scope="col" class="py-3 px-6 w-12"></th> <!-- Copy button column -->
                  </tr>
                </thead>
                <tbody>
                  <tr class="bg-white border-b border-grey-lighter last:border-b-0">
                    <td class="py-4 px-6 w-1/3">A</td>
                    <td class="py-4 px-6 w-1/3 font-mono">&#64;</td>
                    <td class="py-4 px-6 w-1/3 font-mono">45.55.120.81</td>
                    <td class="py-4 px-6 text-right w-12">
                      <button
                        mat-icon-button
                        class="h-6 w-6 leading-6"
                        aria-label="Copy Value"
                        matTooltip="Copy Value"
                        [cdkCopyToClipboard]="'45.55.120.81'"
                        (cdkCopyToClipboardCopied)="copiedToClipboard()">
                        <!-- <mat-icon svgIcon="content_copy" class="h-4 w-4"></mat-icon> -->
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-container>
          <ng-container *ngIf="detailedDomain.name.split('.').length > 2">
            <!-- Step 3: Subdomain (CNAME Record) -->
            <h4 class="text-base font-medium mt-6 mb-2">Step 3: Add these new DNS records</h4>
            <div class="overflow-x-auto relative border border-grey-lighter rounded">
              <table class="w-full text-sm text-left text-ink table-fixed">
                <thead class="text-xs text-smoke-dark uppercase bg-grey-lightest">
                  <tr>
                    <th scope="col" class="py-3 px-6 w-1/3">Type</th>
                    <th scope="col" class="py-3 px-6 w-1/3">Name</th>
                    <th scope="col" class="py-3 px-6 w-1/3">Current Value</th>
                    <th scope="col" class="py-3 px-6 w-12"></th> <!-- Copy button column -->
                  </tr>
                </thead>
                <tbody>
                  <tr class="bg-white border-b border-grey-lighter last:border-b-0">
                    <td class="py-4 px-6 w-1/3">CNAME</td>
                    <td class="py-4 px-6 w-1/3 font-mono">{{ detailedDomain.name.split('.')[0] }}</td> <!-- Extract subdomain part -->
                    <td class="py-4 px-6 w-1/3 font-mono">cname.hospitable.rentals</td>
                    <td class="py-4 px-6 text-right w-12">
                      <button
                        mat-icon-button
                        class="h-6 w-6 leading-6"
                        aria-label="Copy Value"
                        matTooltip="Copy Value"
                        [cdkCopyToClipboard]="'cname.hospitable.rentals'"
                        (cdkCopyToClipboardCopied)="copiedToClipboard()">
                        <!-- <mat-icon svgIcon="content_copy" class="h-4 w-4"></mat-icon> -->
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-container>

          <p class="text-xs italic text-smoke-dark mt-4">
            Note: DNS changes can take up to 48 hours to propagate globally. After updating your records, click 'Verify site'
            above.
          </p>
        </div>
      </ng-container>

      <!-- Show Verified Description Block if verified successfully -->
      <ng-container *ngIf="dnsVerifiedSuccessfully">
        <sbnb-direct-domain-description
          [isPublished]="directEditService.isPublished"
          [url]="'https://' + detailedDomain.name" />
      </ng-container>
    </ng-container>
  </ng-container>
  <ng-container *ngIf="!detailedDomain && domains">
    <sbnb-direct-domain-description
      [isPublished]="directEditService.isPublished"
      [url]="'https://' + domains.subdomain + '.' + domains.hospitable_domain" />
  </ng-container>
</ng-container>
